import { FlowNames } from '@frontend-tools/enum/flow.enum';
import { Funnel } from '@frontend-tools/enum/funnel.enum';
import { amplitudePushEvent, gtmPushEvent } from '@kavak/frontend-layout';

interface Metadata {
  [key: string]: string | number | boolean | undefined;
}

let _userEmailHash: string | undefined;
let _userPhoneHash: string | undefined;
let _deviceType: string | undefined;

export const setUserHashes = (emailHash?: string, phoneHash?: string): void => {
  _userEmailHash = emailHash;
  _userPhoneHash = phoneHash;
};

/**
 * Tipo de dispositivo que resuelve la plataforma (header `x-device-type`):
 * `desktop` | `mobile` | `webview`.
 *
 * Se setea una sola vez desde el layout raiz y `pushEvent` lo agrega a todos
 * los eventos, para poder cortar cualquier metrica por dispositivo. Antes no
 * habia forma: ninguno de nuestros eventos mandaba el dato.
 */
export const setDeviceType = (deviceType?: string): void => {
  _deviceType = deviceType;
};

const flows = {
  [Funnel.FINANCING_FIRST]: FlowNames.ffOnline,
  [Funnel.AUTO_FIRST]: FlowNames.autoFirst,
};

export const getFlow = (funnel: Funnel): string => {
  return flows[funnel as keyof typeof flows];
};

// Simple in-session dedupe for "*_viewed" events
const firedViewedEvents = new Set<string>();
export const resetViewedEventDedupe = (): void => {
  firedViewedEvents.clear();
};

export const pushEvent = (
  event: string,
  funnel: Funnel,
  metadata?: Metadata
): void => {
  // Skip duplicate "*_viewed" events within the same session
  if (event?.endsWith('_viewed')) {
    if (firedViewedEvents.has(event)) {
      return;
    }
    firedViewedEvents.add(event);
  }

  const flow = getFlow(funnel);
  try {
    gtmPushEvent({
      track: {
        event,
        flow,
        funnel: 'financing',
        isSecondHolder: false,
        device_type: _deviceType,
        ...metadata,
      },
      debug: false,
    });
  } catch (error) {
    console.error('Failed to push event to GTM:', error);
  }

  try {
    const amplitudeMetadata = { ...(metadata ?? {}) };
    delete amplitudeMetadata.userEmail;
    delete amplitudeMetadata.userPhone;
    amplitudePushEvent({
      event,
      flow,
      funnel: 'financing',
      device_type: _deviceType,
      ...amplitudeMetadata,
    });
  } catch (error) {
    console.error('Failed to push event to Amplitude:', error);
  }
};

/**
 * Exposiciones ya emitidas en esta carga de pagina, por `flagKey:variant`.
 *
 * A diferencia del dedupe de `*_viewed`, este NO se resetea entre steps: una
 * exposicion describe al usuario frente al flag, no a la pantalla, asi que
 * volver a emitirla no aporta nada. Amplitude igual cuenta usuarios unicos
 * expuestos, el dedupe es solo para no mandar ruido.
 */
const firedExposures = new Set<string>();

/**
 * Evento de exposicion de Amplitude Experiment (`$exposure`).
 *
 * La asignacion de variante se resuelve en el server (`/api/[site]/[step]`),
 * asi que en el browser nunca corre el SDK de experimentos y nadie emite la
 * exposicion. Sin este evento la pestania de analisis del experimento se queda
 * en "No data has been received from exposed users yet": Amplitude sabe a quien
 * asigno, pero no a quien le llego a mostrar la variante.
 *
 * No pasa por `pushEvent` a proposito. `$exposure` lo consume el motor de
 * experimentos, no analitica de producto: meterlo en el dataLayer de GTM solo
 * dispararia tags que no lo esperan.
 *
 * `variant` es obligatorio y tiene que ser una variante real del experimento.
 * Amplitude lee una exposicion sin variante como DES-exposicion y saca al
 * usuario del analisis, asi que nunca hay que llamar a esto por un usuario sin
 * asignar.
 */
export const pushExposureEvent = ({
  flagKey,
  variant,
}: {
  flagKey: string;
  variant: string;
}): void => {
  if (!flagKey || !variant) return;

  const exposureKey = `${flagKey}:${variant}`;
  if (firedExposures.has(exposureKey)) return;
  firedExposures.add(exposureKey);

  try {
    amplitudePushEvent({
      event: '$exposure',
      flag_key: flagKey,
      variant,
    });
  } catch (error) {
    console.error('Failed to push exposure event to Amplitude:', error);
  }
};
